import { useEffect, useRef, useState } from "react";
import { GraduationCap, Camera, Upload, Loader2, CheckCircle2, XCircle, ShieldCheck } from "lucide-react";
import { toast } from "sonner";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { api, errMsg } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { fmtDate } from "@/lib/constants";

const CameraDialog = ({ open, onOpenChange, onCapture }) => {
  const videoRef = useRef(null);
  const [error, setError] = useState(null);
  const [ready, setReady] = useState(false);
  useEffect(() => {
    if (!open) return;
    let stream;
    setError(null);
    setReady(false);
    navigator.mediaDevices?.getUserMedia({ video: { facingMode: "environment", width: { ideal: 1920 } } })
      .then((s) => { stream = s; if (videoRef.current) videoRef.current.srcObject = s; })
      .catch(() => setError("Impossible d'accéder à la caméra. Autorise-la ou importe une photo."));
    return () => stream?.getTracks().forEach((t) => t.stop());
  }, [open]);
  const snap = () => {
    const v = videoRef.current;
    if (!v?.videoWidth) return;
    const c = document.createElement("canvas");
    c.width = v.videoWidth;
    c.height = v.videoHeight;
    c.getContext("2d").drawImage(v, 0, 0);
    c.toBlob((b) => { onOpenChange(false); onCapture(b); }, "image/jpeg", 0.92);
  };
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="rounded-3xl sm:max-w-xl" data-testid="student-camera-dialog">
        <DialogHeader>
          <DialogTitle>Scanner ta carte étudiante</DialogTitle>
          <DialogDescription>Place la carte dans le cadre, bien éclairée, sans reflet. Ton nom et la date de validité doivent être lisibles.</DialogDescription>
        </DialogHeader>
        {error ? <p className="rounded-2xl bg-destructive/10 p-4 text-sm text-destructive">{error}</p> : (
          <div className="relative overflow-hidden rounded-2xl bg-black">
            <video ref={videoRef} autoPlay playsInline muted onLoadedData={() => setReady(true)} className="aspect-video w-full object-cover" data-testid="student-camera-video" />
            <div className="pointer-events-none absolute inset-[12%] rounded-2xl border-2 border-dashed border-white/80" />
          </div>
        )}
        <button data-testid="student-camera-capture" disabled={!!error || !ready} onClick={snap} className="bg-brand inline-flex items-center justify-center gap-2 rounded-full px-6 py-3 text-sm font-semibold text-white disabled:opacity-50">
          {ready || error ? <Camera className="h-4 w-4" /> : <Loader2 className="h-4 w-4 animate-spin" />} {ready || error ? "Prendre la photo" : "Ouverture de la caméra…"}
        </button>
      </DialogContent>
    </Dialog>
  );
};

export const StudentOffer = () => {
  const { user, setUser } = useAuth();
  const fileRef = useRef(null);
  const [camera, setCamera] = useState(false);
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState(null);

  const send = async (blob) => {
    if (!blob) return;
    setBusy(true);
    setResult(null);
    const fd = new FormData();
    fd.append("image", blob, "carte.jpg");
    try {
      const { data } = await api.post("/student/verify", fd, { timeout: 120000 });
      setUser(data.user);
      setResult(data);
      data.approved ? toast.success("Carte vérifiée : −20 % sur ton abonnement !") : toast.error(data.message);
    } catch (e) { toast.error(errMsg(e)); } finally { setBusy(false); }
  };

  if (user?.student_verified) return (
    <div className="card-flat mt-8 flex items-center gap-4 border-emerald-500/40 p-6" data-testid="student-offer-verified">
      <span className="inline-flex h-11 w-11 shrink-0 items-center justify-center rounded-xl bg-emerald-500/15 text-emerald-600"><GraduationCap className="h-5 w-5" /></span>
      <div>
        <p className="font-semibold">Offre étudiante active — −20 % sur Premium et Pro</p>
        <p className="text-sm text-muted-foreground">{user.student_institution ? `${user.student_institution} · ` : ""}valable jusqu'au {fmtDate(user.student_until)}.</p>
      </div>
    </div>
  );

  return (
    <div className="card-flat mt-8 p-6" data-testid="student-offer-card">
      <div className="flex flex-col gap-4 sm:flex-row sm:items-center">
        <span className="bg-brand inline-flex h-11 w-11 shrink-0 items-center justify-center rounded-xl text-white"><GraduationCap className="h-5 w-5" /></span>
        <div className="flex-1">
          <p className="font-semibold">Offre étudiante : −20 % sur Premium et Pro</p>
          <p className="text-sm text-muted-foreground">Scanne ta carte étudiante (collège, lycée, université…) de cette année. Le nom doit correspondre à celui de ton compte ({user?.name}).</p>
        </div>
        <div className="flex flex-wrap gap-2">
          <button data-testid="student-scan-button" disabled={busy} onClick={() => setCamera(true)} className="bg-brand inline-flex items-center gap-2 rounded-full px-5 py-2.5 text-sm font-semibold text-white disabled:opacity-50">
            {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <Camera className="h-4 w-4" />} {busy ? "Vérification…" : "Scanner ma carte"}
          </button>
          <button data-testid="student-upload-button" disabled={busy} onClick={() => fileRef.current?.click()} className="inline-flex items-center gap-2 rounded-full border px-5 py-2.5 text-sm font-semibold hover:border-primary/40 disabled:opacity-50">
            <Upload className="h-4 w-4" /> Importer une photo
          </button>
          <input ref={fileRef} type="file" accept="image/jpeg,image/png,image/webp" capture="environment" className="hidden" data-testid="student-file-input"
            onChange={(e) => { send(e.target.files?.[0]); e.target.value = ""; }} />
        </div>
      </div>
      {result && !result.approved && (
        <p data-testid="student-verify-result" className="mt-4 flex gap-2 rounded-2xl bg-destructive/10 p-3.5 text-sm text-destructive"><XCircle className="h-4 w-4 shrink-0" /> {result.message}</p>
      )}
      {result?.approved && <p data-testid="student-verify-result" className="mt-4 flex gap-2 text-sm text-emerald-600"><CheckCircle2 className="h-4 w-4" /> {result.message}</p>}
      <p className="mt-4 flex gap-2 text-xs text-muted-foreground"><ShieldCheck className="h-4 w-4 shrink-0 text-primary" /> La photo est analysée automatiquement puis supprimée : elle n'est jamais conservée. 5 essais par jour.</p>
      <CameraDialog open={camera} onOpenChange={setCamera} onCapture={send} />
    </div>
  );
};
